import * as THREE from 'three';
import { InteractiveStar } from './InteractiveStar.js';

const SKY_RADIUS = 800;

function azAlt(azDeg, altDeg) {
  const az = (azDeg * Math.PI) / 180;
  const alt = (altDeg * Math.PI) / 180;
  const cosAlt = Math.cos(alt);
  return new THREE.Vector3(
    SKY_RADIUS * cosAlt * Math.sin(az),
    SKY_RADIUS * Math.sin(alt),
    -SKY_RADIUS * cosAlt * Math.cos(az),
  );
}

// === Card content ===

const FIRST_CONVERSATION_CARD = `
  <h2>Jab We Met</h2>
  <div class="label">March 28, 2017</div>

  <p>This is the day we first spoke. The light from this star is exactly nine years old.</p>

  <p>You were nineteen. You did not know yet.</p>
`;

const DDAY_CARD = `
  <h2>D-Day</h2>
  <div class="label">March 3, 2019</div>

  <p>You said yes. You did not need a candlelit dinner. You laughed and you said yes.</p>
`;

const ERIDANI_CARD = `
  <h2>40 Eridani</h2>
  <div class="label">A real star</div>
  <p>Hi, question?</p>
`;

const TAU_CETI_CARD = `
  <h2>Tau Ceti</h2>
  <div class="label">A real star</div>
  <p>The destination.</p>
`;

const HAIL_MARY_CARD = `
  <h2>The Hail Mary</h2>
  <div class="label">A small ship, very far away</div>
  <p>They made it home.</p>
`;

const ASTROPHAGE_CARD = `
  <h2>Astrophage</h2>
  <div class="label">A glow that should not be there</div>
  <p>Microscopic life. Hungry.</p>
  <p>They figured it out.</p>
`;

const XENONITE_CARD = `
  <h2>Xenonite</h2>
  <div class="label">A small faceted thing</div>
  <p>Built by hand.</p>
  <p>Built with care.</p>
`;

const FISTBUMP_CARD = `
  <h2>...</h2>
  <div class="label">Two points, side by side, never touching</div>
  <p>Fistbump.</p>
`;

// === Helper to make a dim hidden star ===
function makeHidden({ position, color = 0x6878a0, glowColor = '120, 140, 180', scale = 10, discoveryId, cardHTML, onClick }) {
  return new InteractiveStar({
    position,
    color,
    glowColor,
    scale,
    pulseSpeed: 0.4,
    pulseAmount: 0.02, // barely pulses
    discoveryId,
    category: 'hidden',
    cardHTML,
    onClick,
  });
}

/**
 * Create all 8 Easter eggs hidden in the sky.
 *
 * Personal moments (2):
 *  - First conversation binary (March 28, 2017) - near Bangalore Planet
 *  - D-Day (March 3, 2019) - quiet corner of the sky
 *
 * Project Hail Mary references (6):
 *  - 40 Eridani - real star, real position (Rocky's home)
 *  - Tau Ceti - real star, real position (Hail Mary's destination)
 *  - The Hail Mary ship - tiny silhouette far from anything
 *  - Astrophage glow - near Jupiter (the Sun-like body in our scene)
 *  - Xenonite shimmer - quiet corner
 *  - The Fistbump - two points side by side that never touch
 *
 * Each egg is small, dim, and unmarked - meant to be discovered by
 * accident on multiple visits. The InteractiveStar pattern is reused with
 * small scale (10-12) and dim cool colors so they blend into the sky.
 *
 * Returns a flat array of all egg objects to be added to the sky.
 */
export function createEasterEggs({ starField, onClick }) {
  const eggs = [];

  // === 1. March 28, 2017 - first conversation binary ===
  // Two close stars near the Bangalore Planet (which is at az 140, alt 35)
  const banglPos = azAlt(143, 38);
  const offset = new THREE.Vector3(5, 0, 0);
  eggs.push(makeHidden({
    position: banglPos.clone().add(offset),
    color: 0xc0a060,
    glowColor: '180, 150, 90',
    scale: 11,
    discoveryId: 'first-conversation',
    cardHTML: FIRST_CONVERSATION_CARD,
    onClick,
  }));
  eggs.push(makeHidden({
    position: banglPos.clone().sub(offset),
    color: 0xc0a060,
    glowColor: '180, 150, 90',
    scale: 11,
    discoveryId: 'first-conversation',
    cardHTML: FIRST_CONVERSATION_CARD,
    onClick,
  }));

  // === 2. March 3, 2019 - D-Day ===
  // Quiet corner of the south-southwest sky, alone
  eggs.push(makeHidden({
    position: azAlt(195, 50),
    color: 0xc0a060,
    glowColor: '180, 150, 90',
    scale: 10,
    discoveryId: 'd-day',
    cardHTML: DDAY_CARD,
    onClick,
  }));

  // === 3. 40 Eridani - Rocky's home in PHM ===
  // Try to find the real star in the catalog
  const eridani = starField.findByBayer('40Omi2Eri') || starField.findByBayer('40Eri');
  if (eridani) {
    eggs.push(makeHidden({
      position: starField.getStarPosition(eridani),
      color: 0xa0b8d0,
      glowColor: '160, 184, 208',
      scale: 9,
      discoveryId: '40-eridani',
      cardHTML: ERIDANI_CARD,
      onClick,
    }));
  }

  // === 4. Tau Ceti - destination in PHM ===
  const tauCeti = starField.findByBayer('TauCet') || starField.findByBayer('Tau Cet');
  if (tauCeti) {
    eggs.push(makeHidden({
      position: starField.getStarPosition(tauCeti),
      color: 0xa0b8d0,
      glowColor: '160, 184, 208',
      scale: 9,
      discoveryId: 'tau-ceti',
      cardHTML: TAU_CETI_CARD,
      onClick,
    }));
  }

  // === 5. The Hail Mary - tiny ship far from anything ===
  // Far north, very high (az 0, alt 80) - almost overhead, alone
  eggs.push(makeHidden({
    position: azAlt(2, 80),
    color: 0x9098a8,
    glowColor: '144, 152, 168',
    scale: 8,
    discoveryId: 'hail-mary',
    cardHTML: HAIL_MARY_CARD,
    onClick,
  }));

  // === 6. Astrophage glow - near Jupiter (the Sun-like body in our scene) ===
  const jupiterPos = starField.getPlanetPosition('Jupiter');
  if (jupiterPos) {
    const astrophagePos = jupiterPos.clone().add(new THREE.Vector3(-12, -10, 0));
    eggs.push(makeHidden({
      position: astrophagePos,
      color: 0xa090c8,
      glowColor: '160, 144, 200',
      scale: 8,
      discoveryId: 'astrophage',
      cardHTML: ASTROPHAGE_CARD,
      onClick,
    }));
  }

  // === 7. Xenonite shimmer - quiet corner of the west sky ===
  eggs.push(makeHidden({
    position: azAlt(285, 45),
    color: 0xb0a8c8,
    glowColor: '176, 168, 200',
    scale: 9,
    discoveryId: 'xenonite',
    cardHTML: XENONITE_CARD,
    onClick,
  }));

  // === 8. The Fistbump - two points side by side, never touching ===
  // Far from anything else
  const fistCenter = azAlt(310, 25);
  const fistOffset = new THREE.Vector3(7, 0, 0);
  eggs.push(makeHidden({
    position: fistCenter.clone().add(fistOffset),
    color: 0x9890c0,
    glowColor: '152, 144, 192',
    scale: 9,
    discoveryId: 'fistbump',
    cardHTML: FISTBUMP_CARD,
    onClick,
  }));
  eggs.push(makeHidden({
    position: fistCenter.clone().sub(fistOffset),
    color: 0x9890c0,
    glowColor: '152, 144, 192',
    scale: 9,
    discoveryId: 'fistbump',
    cardHTML: FISTBUMP_CARD,
    onClick,
  }));

  return eggs;
}
